// Importing necessary libraries and components
import React, { useState, useEffect } from 'react';
import Modal from 'react-modal'; // Modal component for displaying status update form
import { FaTimes } from 'react-icons/fa'; // Icons for closing
import axios from 'axios'; // axios for making HTTP requests

const StatusLabel = ({ caseId }) => {
  // State to control modal visibility (open or closed)
  const [isModalOpen, setIsModalOpen] = useState(false);

  // State to store the current selected hold status option
  const [selectedStatus, setSelectedStatus] = useState('Not On Hold');

  // State to store the custom status when "Other" is selected
  const [customStatus, setCustomStatus] = useState('');

  // State to store the display status (what is shown outside the modal)
  const [currentStatus, setCurrentStatus] = useState('Not On Hold');

  // State to store the status update timestamp
  const [statusUpdatedAt, setStatusUpdatedAt] = useState(null);

  // List of hold options from the image
  const holdOptions = [
    'Not On Hold',
    'Metal try-in',
    'Bite block',
    'Die trim',
    'Frame try-in',
    'Problem with case',
    'Hold Case',
    'In lab - no date yet',
    'Hold for model mod.',
    'Hold for study mod.',
    'Hold for work. mod.',
    'Hold for picture',
    'Hold for shade',
    'Hold for bite',
    'Hold for components',
    'Hold for Impression',
    'Hold for partial',
    'Wax try-in',
    'Dr. requested call',
    'Bisque bake try-in',
    'Other'
  ];

  // useEffect hook to fetch the current status when the component mounts or caseId changes
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        // Fetching the current case status from the API
        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}`);
        const fetchedStatus = response.data.status || 'in progress';
        const fetchedUpdatedAt = response.data.statusUpdatedAt; // Fetch the timestamp

        if (fetchedStatus === 'in progress') {
          setSelectedStatus('Not On Hold');
          setCurrentStatus('Not On Hold');
        } else {
          if (holdOptions.includes(fetchedStatus)) {
            setSelectedStatus(fetchedStatus);
          } else {
            setSelectedStatus('Other');
            setCustomStatus(fetchedStatus);
          }
          setCurrentStatus(fetchedStatus);
        }

        setStatusUpdatedAt(fetchedUpdatedAt ? new Date(fetchedUpdatedAt).toLocaleString() : 'N/A');
      } catch (error) {
        console.error('❌ Error fetching case status:', error);
      }
    };

    // Call the fetch function
    fetchStatus();
  }, [caseId]); // Dependency array ensures this runs when the caseId changes

  // Function to open the modal (to change the status)
  const openModal = () => setIsModalOpen(true);

  // Function to close the modal
  const closeModal = () => setIsModalOpen(false);

  // Function to handle form submission (updating the case status)
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevents the default form submission behavior

    let newStatus;
    if (selectedStatus === 'Not On Hold') {
      newStatus = 'in progress';
    } else if (selectedStatus === 'Other') {
      if (!customStatus.trim()) {
        alert('Please enter a custom status');
        return;
      }
      newStatus = customStatus;
    } else {
      newStatus = selectedStatus;
    }

    try {
      // Making an API call to update the status for the specific case
      const response = await axios.put(`https://hms.backend.hiragroupcloud.com/api/cases/updateStatus/${caseId}`, { status: newStatus });

      alert('Status updated successfully'); // Alert to confirm success

      // Update the display status and timestamp from the response
      const updatedCase = response.data.case;
      const updatedStatus = updatedCase.status === 'in progress' ? 'Not On Hold' : updatedCase.status;
      setCurrentStatus(selectedStatus === 'Not On Hold' ? 'Not On Hold' : selectedStatus === 'Other' ? customStatus : selectedStatus);
      setStatusUpdatedAt(new Date(updatedCase.statusUpdatedAt).toLocaleString());

      closeModal(); // Close the modal after successful submission
    } catch (error) {
      console.error('❌ Error updating status:', error);
      alert('Failed to update status'); // Alert to indicate failure
    }
  };

  return (
    <div>
      {/* Displaying the label for the case status */}
      <p style={{ fontWeight: 'bold' }}>Status:</p>

      {/* Displaying the current status, timestamp, and a button to toggle the modal */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <p className='me-3'>{currentStatus} </p> 
        {/* Button to open the modal */}
        <button
          style={{
            cursor: 'pointer',
            backgroundColor: '#004aad',
            color: 'white',
            padding: '5px 8px',
            borderRadius: '4px',
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderColor: "#004aad",
            marginBottom: "13px",
            border: 'none'
          }}
          onClick={openModal} // Opens the modal
          title="Change Status" // Tooltip text
        >
          Change
        </button>
      </div>

      {/* Modal component to change the case status */}
      <Modal
        isOpen={isModalOpen} // Controls if the modal is visible
        onRequestClose={closeModal} // Closes modal on outside click
        contentLabel="On Hold Status" // Label for the modal (for accessibility)
        style={{
          overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000 }, // Styling for modal overlay
          content: { width: '30%', margin: 'auto', borderRadius: '4px', padding: '0', height: "auto", maxHeight: "60%" }, // Styling for modal content (no padding to allow header to fit)
        }}
      >
        {/* Modal Header */}
        <div style={{
          backgroundColor: '#004aad',
          color: 'white',
          padding: '10px 15px',
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h4 style={{ margin: 0 }}>On Hold Status</h4>
          <button onClick={closeModal} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: 'white' }}>
            <FaTimes /> {/* Close icon */}
          </button>
        </div>

        {/* Form to update the case status */}
        <form onSubmit={handleSubmit} style={{ padding: '15px' }}>
          <div style={{ marginBottom: '15px' }}>
            {/* Label and dropdown select for status */}
            <label style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold' }}>Status *</label>
            <select
              value={selectedStatus} // Controlled value, linked to state
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                if (e.target.value !== 'Other') setCustomStatus('');
              }} // Handle status change
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#f9f9f9',
                fontSize: '16px',
              }}
            >
              {/* Dropdown options for different status values */}
              {holdOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Custom Status input if "Other" is selected */}
          {selectedStatus === 'Other' && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold' }}>Custom Status *</label>
              <textarea
                value={customStatus}
                onChange={(e) => setCustomStatus(e.target.value)}
                rows={4}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9',
                  fontSize: '16px',
                }}
              />
            </div>
          )}

          {/* Buttons: Cancel and Save */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              type="button"
              onClick={closeModal}
              style={{
                backgroundColor: '#ccc',
                color: 'black',
                border: 'none',
                padding: '10px 20px',
                cursor: 'pointer',
                borderRadius: '4px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit" // Triggers form submission
              style={{
                backgroundColor: '#004aad',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                cursor: 'pointer',
                borderRadius: '4px',
              }}
            >
              Save
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default StatusLabel; 